import {
  LOCAL_STORAGE_DEMO_PRIVATE_KEY,
  LOCAL_STORAGE_MEMBER_AUTH_SESSION,
} from "../components/constants";

const AUTH_UNAUTHORIZED_EVENT = "member-auth-unauthorized";

const isDevelopment = () => process.env.REACT_APP_ENV === "development";

const getBaseURL = () => {
  const isLocalhost = window.location.hostname === "localhost";
  return isLocalhost || isDevelopment()
    ? "https://artemis-api.ektimiseto.gr:444"
    : "https://artemis-api.ektimiseto.gr";
};

const parseJsonSafely = async (response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const buildError = (response, parsedError) => {
  return {
    ...(parsedError || {}),
    status: response.status,
    message:
      parsedError?.message || `Request failed with status ${response.status}`,
  };
};

const getStoredAuthSession = () => {
  const rawSession = localStorage.getItem(LOCAL_STORAGE_MEMBER_AUTH_SESSION);
  if (!rawSession) return null;

  try {
    return JSON.parse(rawSession);
  } catch {
    localStorage.removeItem(LOCAL_STORAGE_MEMBER_AUTH_SESSION);
    return null;
  }
};

const resolveClientId = ({ account, member }) =>
  account?.clientId || account?.id || member?.clientId || member?.accountId;

const resolvePermissions = ({ permissions, member, account }) => {
  if (Array.isArray(permissions) && permissions.length > 0) {
    return permissions;
  }

  if (Array.isArray(member?.permissions) && member.permissions.length > 0) {
    return member.permissions;
  }

  if (Array.isArray(account?.permissions) && account.permissions.length > 0) {
    return account.permissions;
  }

  return [];
};

export const setStoredAuthSession = ({
  token,
  member,
  account,
  permissions,
}) => {
  const normalizedSession = {
    token,
    member: member || null,
    account: account || null,
    clientId: resolveClientId({ member, account }) || null,
    permissions: resolvePermissions({ permissions, member, account }),
  };

  localStorage.setItem(
    LOCAL_STORAGE_MEMBER_AUTH_SESSION,
    JSON.stringify(normalizedSession),
  );

  return normalizedSession;
};

export const clearStoredAuthSession = () => {
  localStorage.removeItem(LOCAL_STORAGE_MEMBER_AUTH_SESSION);
};

export const getStoredAuthToken = () => {
  return getStoredAuthSession()?.token || null;
};

export const getStoredMemberSession = () => {
  return getStoredAuthSession();
};

const notifyUnauthorized = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT));
  }
};

export const MEMBER_AUTH_UNAUTHORIZED_EVENT = AUTH_UNAUTHORIZED_EVENT;

async function apiRequest({
  endpoint,
  method = "GET",
  params = null,
  includeAuth = false,
  usePrivateKeyAuth = false,
  responseType = "json",
}) {
  let url = `${getBaseURL()}${endpoint}`;
  const options = {
    method,
    headers: {
      "Content-Type": "application/json",
    },
  };

  if (includeAuth && !usePrivateKeyAuth) {
    const token = getStoredAuthToken();
    if (token) {
      options.headers.Authorization = `Bearer ${token}`;
    }
  }

  if (usePrivateKeyAuth) {
    const privateKey = localStorage.getItem(LOCAL_STORAGE_DEMO_PRIVATE_KEY);
    if (privateKey) {
      options.headers["X-Private-Key"] = privateKey;
    }
  }

  if (method === "GET" && params) {
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== undefined),
    );
    const query = new URLSearchParams(filteredParams).toString();
    if (query) {
      url += `?${query}`;
    }
  } else if (params) {
    options.body = JSON.stringify(params);
  }

  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      const parsedError = await parseJsonSafely(response);
      const errorPayload = buildError(response, parsedError);

      if (response.status === 401 && includeAuth && !usePrivateKeyAuth) {
        clearStoredAuthSession();
        notifyUnauthorized();
      }

      return Promise.reject(errorPayload);
    }

    if (responseType === "blob") {
      return Promise.resolve(response);
    }

    const parsedResponse = await parseJsonSafely(response);
    return Promise.resolve(parsedResponse || {});
  } catch (error) {
    console.error("API Request Error:", error);
    throw error;
  }
}

export async function loadGeoJSON(name) {
  const { url } = await apiRequest({ endpoint: `/files/${name}` });

  const data = await fetch(url).then((res) => res.json());

  return data;
}

export const getRiskAssessment = ({
  floor,
  address,
  latitude,
  longitude,
  language,
  egsa87x,
  egsa87y,
  name,
  notes,
}) => {
  return apiRequest({
    endpoint: "/risk-assessment/demo",
    method: "POST",
    params: {
      floor,
      address,
      latitude,
      longitude,
      language,
      egsa87x,
      egsa87y,
      name,
      notes,
    },
    includeAuth: true,
  });
};

// Partial update of an assessment's user metadata (name / notes). Returns the
// updated row serialized like the GET history items.
export const updateRiskAssessment = ({ clientId, assessmentId, name, notes }) => {
  return apiRequest({
    endpoint: `/ui/risk-assessment/${clientId}/${assessmentId}`,
    method: "PATCH",
    params: { name, notes },
    includeAuth: true,
  });
};

// Area (polygon) risk assessment. Uses the same endpoint as the point flow —
// the server branches on the `polygons` payload. `polygons` is a GeoJSON
// FeatureCollection whose features carry per-polygon `area_sqm`/`perimeter_m`
// in their `properties`.
export const getAreaRiskAssessment = ({
  floor,
  language,
  polygons,
  name,
  notes,
}) => {
  return apiRequest({
    endpoint: "/risk-assessment/demo",
    method: "POST",
    params: { floor, language, polygons, name, notes },
    includeAuth: true,
  });
};

// Server-side (geodesic) measurement of drawn polygons. `polygons` is a
// GeoJSON FeatureCollection; the response echoes it back with per-feature
// `properties.area_sqm` / `properties.perimeter_m` populated, plus totals.
export const measureGeometry = ({ polygons }) => {
  return apiRequest({
    endpoint: "/ui/geometry/measure",
    method: "POST",
    params: { polygons },
    includeAuth: true,
  });
};

export const validateDemoKey = (privateKey) => {
  return apiRequest({
    endpoint: "/validate-demo-key",
    method: "GET",
    params: { privateKey },
  });
};

export const getMunicipalityProperties = ({ clientId, ...params }) => {
  if (!params?.portfolioId) {
    return Promise.reject(new Error("portfolioId query param is required"));
  }

  return apiRequest({
    endpoint: `/ui/portfolio/properties/${clientId}`,
    method: "GET",
    params,
    includeAuth: true,
  });
};

// Helper function to download Excel file from export endpoint
export const downloadMunicipalityPropertiesExport = async ({
  clientId,
  ...params
}) => {
  if (!params?.portfolioId) {
    throw new Error("portfolioId query param is required");
  }

  try {
    const response = await apiRequest({
      endpoint: `/ui/portfolio/properties/${clientId}/export`,
      method: "GET",
      params,
      includeAuth: true,
      responseType: "blob",
    });

    // Get the blob from response
    const blob = await response.blob();

    // Create a download link
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;

    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get("Content-Disposition");
    let filename = "properties_export.xlsx";
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Export Error:", error);
    const errorMessage =
      error?.message || "Failed to export properties. Please try again.";
    throw new Error(errorMessage);
  }
};

export const getMunicipalityStats = ({ clientId, ...params }) => {
  if (!params?.portfolioId) {
    return Promise.reject(new Error("portfolioId query param is required"));
  }

  return apiRequest({
    endpoint: `/ui/portfolio/municipalities/${clientId}`,
    method: "GET",
    params,
    includeAuth: true,
  });
};

export const getConfig = ({ clientId }) => {
  return apiRequest({
    endpoint: `/ui/portfolio/config/${clientId}`,
    method: "GET",
    includeAuth: true,
  });
};

export const getRiskAssessmentHistory = ({ clientId, ...params }) => {
  return apiRequest({
    endpoint: `/ui/risk-assessment/${clientId}`,
    method: "GET",
    params,
    includeAuth: true,
  });
};

export const downloadRiskAssessmentHistoryExport = async ({
  clientId,
  ...params
}) => {
  try {
    const response = await apiRequest({
      endpoint: `/ui/risk-assessment/${clientId}/export`,
      method: "GET",
      params,
      includeAuth: true,
      responseType: "blob",
    });

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;

    const contentDisposition = response.headers.get("Content-Disposition");
    let filename = "risk_assessments_export.xlsx";
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Risk Assessment Export Error:", error);
    const errorMessage =
      error?.message || "Failed to export risk assessments. Please try again.";
    throw new Error(errorMessage);
  }
};

export const renamePortfolio = ({ clientId, portfolioId, name }) => {
  return apiRequest({
    endpoint: `/ui/portfolio/config/${clientId}/portfolios/${portfolioId}`,
    method: "PATCH",
    params: { name },
    includeAuth: true,
  });
};

export const loginMember = ({ email, password }) => {
  return apiRequest({
    endpoint: "/auth/login",
    method: "POST",
    params: { email, password },
  });
};

export const registerMember = ({ email, password, privateKey, department }) => {
  return apiRequest({
    endpoint: "/auth/register-member",
    method: "POST",
    params: { email, password, privateKey, department },
  });
};

export const changePassword = ({ currentPassword, newPassword }) => {
  return apiRequest({
    endpoint: "/auth/change-password",
    method: "POST",
    params: { currentPassword, newPassword },
    includeAuth: true,
  });
};

export const forgotPassword = ({ email }) => {
  return apiRequest({
    endpoint: "/auth/forgot-password",
    method: "POST",
    params: { email },
  });
};

export const resetPassword = ({ token, newPassword }) => {
  return apiRequest({
    endpoint: "/auth/reset-password",
    method: "POST",
    params: { token, newPassword },
  });
};

export const getAuthMe = () => {
  return apiRequest({
    endpoint: "/auth/me",
    method: "GET",
    includeAuth: true,
  });
};

export const validateMemberSession = () => {
  return getAuthMe();
};

export const getGoogleStreetViewMetadata = async ({
  latitude,
  longitude,
  apiKey,
  source = "outdoor",
} = {}) => {
  const lat = Number(latitude);
  const lng = Number(longitude);

  if (!apiKey || !Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  const params = new URLSearchParams({
    location: `${lat},${lng}`,
    key: apiKey,
    source,
  });

  try {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/streetview/metadata?${params.toString()}`,
    );
    if (!response.ok) {
      return null;
    }

    const payload = await response.json();
    return payload?.status === "OK" ? payload : null;
  } catch (error) {
    console.error("Street View metadata request failed:", error);
    return null;
  }
};

// Portfolio Intelligence runs on bundled demo fixtures; there is no backend endpoint.
export const getPortfolioIntelligenceDemoData = async () => {
  const [scenario, brokers, clusters, facts, samples] = await Promise.all([
    import("../components/portfolioIntelligence/data/scenario.json"),
    import("../components/portfolioIntelligence/data/brokers.json"),
    import("../components/portfolioIntelligence/data/clusters.json"),
    import("../components/portfolioIntelligence/data/facts.json"),
    import("../components/portfolioIntelligence/data/samples.json"),
  ]);
  return {
    scenario: scenario.default,
    brokers: brokers.default,
    clusters: clusters.default,
    facts: facts.default,
    samples: samples.default,
  };
};
